import KanbanAPI from '../../api/kanban';

const state = {
  pipelines: [],
  stages: {},
  deals: {},
  products: [],
  stageMessages: {},
  uiFlags: {
    isFetchingPipelines: false,
    isFetchingStages: false,
    isFetchingDeals: false,
    isFetchingProducts: false,
    isFetchingStageMessages: false,
    isCreating: false,
    isUpdating: false,
  },
};

const getters = {
  getPipelines: $state => $state.pipelines,
  getActivePipelines: $state => $state.pipelines.filter(p => p.is_active),
  getStagesByPipeline: $state => pipelineId => $state.stages[pipelineId] || [],
  getDealsByStage: $state => stageId =>
    Object.values($state.deals).filter(
      deal => Number(deal.stage_id) === Number(stageId)
    ),
  getDealsByContact: $state => contactId =>
    Object.values($state.deals).filter(
      deal => Number(deal.contact_id) === Number(contactId)
    ),
  getDeal: $state => dealId => $state.deals[dealId],
  getProducts: $state => $state.products,
  getActiveProducts: $state => $state.products.filter(p => p.is_active),
  getStageMessages: $state => pipelineId =>
    $state.stageMessages[pipelineId] || [],
  getUIFlags: $state => $state.uiFlags,
};

const actions = {
  async fetchPipelines({ commit }) {
    commit('SET_UI_FLAG', { isFetchingPipelines: true });
    try {
      const { data } = await KanbanAPI.getPipelines();
      commit('SET_PIPELINES', data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingPipelines: false });
    }
  },

  async fetchStages({ commit }, pipelineId) {
    commit('SET_UI_FLAG', { isFetchingStages: true });
    try {
      const { data } = await KanbanAPI.getStages(pipelineId);
      commit('SET_STAGES', { pipelineId, stages: data });
    } finally {
      commit('SET_UI_FLAG', { isFetchingStages: false });
    }
  },

  async fetchDeals({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetchingDeals: true });
    try {
      const { data } = await KanbanAPI.getDeals(params);
      commit('SET_DEALS', data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingDeals: false });
    }
  },

  async createPipeline({ commit }, pipelineData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await KanbanAPI.createPipeline(pipelineData);
      commit('ADD_PIPELINE', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async updatePipeline({ commit }, { id, ...pipelineData }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const { data } = await KanbanAPI.updatePipeline(id, pipelineData);
      commit('UPDATE_PIPELINE', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async deletePipeline({ commit }, pipelineId) {
    await KanbanAPI.deletePipeline(pipelineId);
    commit('DELETE_PIPELINE', pipelineId);
  },

  async createDeal({ commit }, dealData) {
    commit('SET_UI_FLAG', { isCreating: true });
    try {
      const { data } = await KanbanAPI.createDeal(dealData);
      commit('ADD_DEAL', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isCreating: false });
    }
  },

  async updateDeal({ commit }, { id, ...dealData }) {
    commit('SET_UI_FLAG', { isUpdating: true });
    try {
      const { data } = await KanbanAPI.updateDeal(id, dealData);
      commit('UPDATE_DEAL', data);
      return data;
    } finally {
      commit('SET_UI_FLAG', { isUpdating: false });
    }
  },

  async moveDeal({ commit }, { dealId, stageId }) {
    const { data } = await KanbanAPI.moveDeal(dealId, stageId);
    commit('UPDATE_DEAL', data);
    return data;
  },

  async wonDeal({ commit }, dealId) {
    const { data } = await KanbanAPI.wonDeal(dealId);
    commit('UPDATE_DEAL', data);
    return data;
  },

  async lostDeal({ commit }, { dealId, lostReason }) {
    const { data } = await KanbanAPI.lostDeal(dealId, lostReason);
    commit('UPDATE_DEAL', data);
    return data;
  },

  async deleteDeal({ commit }, dealId) {
    await KanbanAPI.deleteDeal(dealId);
    commit('DELETE_DEAL', dealId);
  },

  // Products
  async fetchProducts({ commit }, params = {}) {
    commit('SET_UI_FLAG', { isFetchingProducts: true });
    try {
      const { data } = await KanbanAPI.getProducts(params);
      commit('SET_PRODUCTS', data);
    } finally {
      commit('SET_UI_FLAG', { isFetchingProducts: false });
    }
  },

  async createProduct({ commit }, productData) {
    const { data } = await KanbanAPI.createProduct(productData);
    commit('ADD_PRODUCT', data);
    return data;
  },

  async updateProduct({ commit }, { id, ...productData }) {
    const { data } = await KanbanAPI.updateProduct(id, productData);
    commit('UPDATE_PRODUCT', data);
    return data;
  },

  async deleteProduct({ commit }, productId) {
    await KanbanAPI.deleteProduct(productId);
    commit('DELETE_PRODUCT', productId);
  },

  // Stage Messages
  async fetchStageMessages({ commit }, pipelineId) {
    commit('SET_UI_FLAG', { isFetchingStageMessages: true });
    try {
      const { data } = await KanbanAPI.getStageMessages(pipelineId);
      commit('SET_STAGE_MESSAGES', { pipelineId, messages: data });
    } finally {
      commit('SET_UI_FLAG', { isFetchingStageMessages: false });
    }
  },

  async createStageMessage({ commit }, { pipelineId, ...msgData }) {
    const { data } = await KanbanAPI.createStageMessage(pipelineId, msgData);
    commit('ADD_STAGE_MESSAGE', { pipelineId, message: data });
    return data;
  },

  async updateStageMessage({ commit }, { pipelineId, id, ...msgData }) {
    const { data } = await KanbanAPI.updateStageMessage(
      pipelineId,
      id,
      msgData
    );
    commit('UPDATE_STAGE_MESSAGE', { pipelineId, message: data });
    return data;
  },

  async deleteStageMessage({ commit }, { pipelineId, id }) {
    await KanbanAPI.deleteStageMessage(pipelineId, id);
    commit('DELETE_STAGE_MESSAGE', { pipelineId, id });
  },
};

const mutations = {
  SET_UI_FLAG($state, flag) {
    $state.uiFlags = { ...$state.uiFlags, ...flag };
  },

  SET_PIPELINES($state, pipelines) {
    $state.pipelines = pipelines;
  },

  ADD_PIPELINE($state, pipeline) {
    $state.pipelines.push(pipeline);
  },

  UPDATE_PIPELINE($state, pipeline) {
    const idx = $state.pipelines.findIndex(
      item => Number(item.id) === Number(pipeline.id)
    );
    if (idx !== -1) {
      $state.pipelines.splice(idx, 1, pipeline);
    }
  },

  DELETE_PIPELINE($state, pipelineId) {
    $state.pipelines = $state.pipelines.filter(
      item => Number(item.id) !== Number(pipelineId)
    );
  },

  SET_STAGES($state, { pipelineId, stages }) {
    $state.stages = { ...$state.stages, [pipelineId]: stages };
  },

  SET_DEALS($state, deals) {
    const dealsMap = {};
    deals.forEach(deal => {
      dealsMap[deal.id] = deal;
    });
    $state.deals = { ...$state.deals, ...dealsMap };
  },

  ADD_DEAL($state, deal) {
    $state.deals = { ...$state.deals, [deal.id]: deal };
  },

  UPDATE_DEAL($state, deal) {
    $state.deals = { ...$state.deals, [deal.id]: deal };
  },

  DELETE_DEAL($state, dealId) {
    const { [dealId]: _removed, ...rest } = $state.deals;
    $state.deals = rest;
  },

  SET_PRODUCTS($state, products) {
    $state.products = products;
  },

  ADD_PRODUCT($state, product) {
    $state.products.push(product);
  },

  UPDATE_PRODUCT($state, product) {
    const idx = $state.products.findIndex(p => p.id === product.id);
    if (idx !== -1) $state.products.splice(idx, 1, product);
  },

  DELETE_PRODUCT($state, productId) {
    $state.products = $state.products.filter(p => p.id !== productId);
  },

  SET_STAGE_MESSAGES($state, { pipelineId, messages }) {
    $state.stageMessages = { ...$state.stageMessages, [pipelineId]: messages };
  },

  ADD_STAGE_MESSAGE($state, { pipelineId, message }) {
    const list = $state.stageMessages[pipelineId] || [];
    $state.stageMessages = {
      ...$state.stageMessages,
      [pipelineId]: [...list, message],
    };
  },

  UPDATE_STAGE_MESSAGE($state, { pipelineId, message }) {
    const list = ($state.stageMessages[pipelineId] || []).map(m =>
      m.id === message.id ? message : m
    );
    $state.stageMessages = { ...$state.stageMessages, [pipelineId]: list };
  },

  DELETE_STAGE_MESSAGE($state, { pipelineId, id }) {
    const list = ($state.stageMessages[pipelineId] || []).filter(
      m => m.id !== id
    );
    $state.stageMessages = { ...$state.stageMessages, [pipelineId]: list };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
